import { Injectable } from '@angular/core';
import { selectPersistStateInit } from '@datorama/akita';
import { ProviderService, AuthService } from '../services';
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { AuthQuery } from '../services/state/auth/auth.query';
import { ProviderQuery } from '../services/state/provider/provider.query';
import { ProviderConfigurationVo } from 'api-clinician-app';
import { map, tap, switchMap, take, catchError, withLatestFrom } from 'rxjs/operators';
import * as moment from 'moment';
import { ProviderStore } from '../services/state/provider/provider.store';
import { AppInsightTags, ApplicationInsightsService } from '../services/application-insights.service';

@Injectable({
  providedIn: 'root',
})
export class ProviderDataResolverService {
  constructor(
    private providerService: ProviderService,
    private router: Router,
    private authQuery: AuthQuery,
    private providerQuery: ProviderQuery,
    private authService: AuthService,
    private providerStore: ProviderStore,
    private appInsights: ApplicationInsightsService
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ProviderConfigurationVo> {
    const navTenantId = route.paramMap.get('tenantId');
    if (!navTenantId) {
      return of(null);
    }

    return this.ensureProviderForTenant(navTenantId, state.url);
  }

  /**
   * Ensures the provider for `navTenantId` is loaded into the store. Switching is allowed
   * for super-admins or users with a role scoped to the target tenant; otherwise refused
   * with a single loop-safe redirect. Shared with HBClinicalContractGuard so the contract
   * check evaluates the target tenant (guards run before resolvers).
   *
   * The decision waits for persisted-state rehydration: on a cold load unhydrated auth
   * must mean "wait", never "no access". Anonymous users go to login first - the refusal
   * below must not run for them, or its redirect hijacks the navigation and drops the
   * deep link's returnUrl.
   */
  ensureProviderForTenant(navTenantId: string, stateUrl: string): Observable<ProviderConfigurationVo> {
    return selectPersistStateInit().pipe(
      take(1),
      withLatestFrom(this.providerQuery.select(), this.authQuery.select()),
      switchMap(([, provider, auth]) => {
        // Not signed in - defer to the login flow, preserving the deep link.
        if (!auth.authenticated || !auth.token) {
          this.router.navigate(['/auth/login'], { queryParams: { returnUrl: stateUrl } });
          return EMPTY;
        }

        const currentTenantId = auth.currentTenantId;
        const currentDate = moment();

        // Already in this tenant - serve the cached provider, or refresh if stale.
        if (currentTenantId === navTenantId) {
          if (!provider.lastChecked || moment(provider.lastChecked).diff(currentDate, 'minutes') > 5) {
            return this.providerService.getProvider(currentTenantId).pipe(
              tap({ next: () => this.providerStore.update({ lastChecked: currentDate.toDate() }) }) // on success only
            );
          }
          return of(provider.details);
        }

        // Switching tenants - allowed for super-admins and for users with a role in the target tenant.
        const hasAccessToTarget = (auth.roles || []).some(role => role.Scope === navTenantId);
        if (auth.superAdmin || hasAccessToTarget) {
          if (auth.superAdmin) {
            this.appInsights.trackEvent(AppInsightTags.PROVIDER_IMPERSONATION, { targetProviderId: navTenantId });
          }
          this.authService.setCurrentTenantId(navTenantId);
          return this.providerService.getProvider(navTenantId).pipe(
            tap({ next: () => this.providerStore.update({ lastChecked: currentDate.toDate() }) }) // on success only
          );
        }

        // No access to the target tenant - refuse once, without looping.
        const err = 'Attempt to open a provider the user has no access to';
        console.error(err);
        this.appInsights.trackException(new Error(err), 'ProviderDataResolverService.ensureProviderForTenant()', {
          targetProviderId: navTenantId,
        });

        const ownTenantUrl = currentTenantId ? stateUrl.replace(navTenantId, currentTenantId) : null;
        this.router.navigateByUrl(ownTenantUrl && ownTenantUrl !== stateUrl ? ownTenantUrl : '/');
        return EMPTY;
      })
    );
  }
}
